import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getChannelPrefs,
  getSuomiFiEligibility,
  updateChannelPrefs,
  type ChannelPrefs,
} from './channels';
import { getMyFeedback, submitFeedback } from './feedback';
import {
  dismissBanner,
  getActiveBanners,
  getPersonalMessages,
} from './banners';
import { withPrincipal } from 'src/api/principals';

// Comms reads are principal-scoped (REQ-20) — when acting on behalf, you manage
// the principal's channel prefs / see their banners. Keys are prefixed with
// `withPrincipal([...])`; invalidations use the same prefix so they still match.
const K_COMMS_CHANNELS = 'K_COMMS_CHANNELS';
const K_COMMS_SUOMIFI = 'K_COMMS_SUOMIFI';
const K_MY_FEEDBACK = 'K_MY_FEEDBACK';
const K_ACTIVE_BANNERS = 'K_ACTIVE_BANNERS';
const K_PERSONAL_MESSAGES = 'K_PERSONAL_MESSAGES';
const STALE = 5 * 60 * 1000;
const BANNER_STALE = 60 * 1000;

export const useChannelPrefs = () =>
  useQuery({
    queryKey: withPrincipal([K_COMMS_CHANNELS]),
    queryFn: () => getChannelPrefs(),
    staleTime: STALE,
  });

export const useSuomiFiEligibility = () =>
  useQuery({
    queryKey: withPrincipal([K_COMMS_SUOMIFI]),
    queryFn: () => getSuomiFiEligibility(),
    staleTime: STALE,
  });

export const useUpdateChannelPrefs = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateChannelPrefs,
    onSuccess: (data: ChannelPrefs) =>
      qc.setQueryData(withPrincipal([K_COMMS_CHANNELS]), data),
  });
};

// REQ-23: anonymous feedback. The list stub is shipped for a cheap future
// "My feedback" surface; the form-only MVP doesn't render it yet.
export const useMyFeedback = (userId: string) =>
  useQuery({
    queryKey: withPrincipal([K_MY_FEEDBACK, userId]),
    queryFn: () => getMyFeedback({ userId }),
    staleTime: STALE,
  });

export const useSubmitFeedback = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: submitFeedback,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: withPrincipal([K_MY_FEEDBACK]) }),
  });
};

// REQ-22: inbound info banners (receive-side). Short stale — banners are
// time-boxed announcements. Disabled until we have a user to key on.
export const useActiveBanners = (userId: string, propertyId?: string) =>
  useQuery({
    queryKey: withPrincipal([K_ACTIVE_BANNERS, userId, propertyId]),
    queryFn: () => getActiveBanners({ userId, propertyId }),
    staleTime: BANNER_STALE,
    enabled: !!userId,
  });

export const useDismissBanner = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: dismissBanner,
    // Optimistic removal is done in the component (local + localStorage);
    // refetch on settle so the server's own filter takes over.
    onSettled: () =>
      qc.invalidateQueries({ queryKey: withPrincipal([K_ACTIVE_BANNERS]) }),
  });
};

// REQ-22: per-user personal messages — stub-only for now (defer to Phase 5).
export const usePersonalMessages = (userId: string) =>
  useQuery({
    queryKey: withPrincipal([K_PERSONAL_MESSAGES, userId]),
    queryFn: () => getPersonalMessages({ userId }),
    staleTime: BANNER_STALE,
    enabled: !!userId,
  });
